// frontend/src/components/public/blocks/AudienceDemographicsBlock.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import {
  People as PeopleIcon,
  Work as WorkIcon,
  EuroSymbol as MoneyIcon,
  Public as PublicIcon,
  PlayCircle as StreamIcon,
  YouTube as YouTubeIcon,
  TrendingUp as TrendingIcon,
  Visibility as ViewsIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface AudienceDemographicsBlockProps {
  data: {
    ageDistribution: {
      data: Array<{
        ageRange: string;
        percentage: number;
      }>;
      showPieChart: boolean;
    };
    professionalProfile: {
      data: Array<{
        category: string;
        percentage: number;
      }>;
      showPieChart: boolean;
    };
    averageSpend: number;
    averageSpendBgColor?: string;  // ✅ NEW FIELD
    geographicReach: {
      data: Array<{
        location: string;
        percentage: number;
      }>;
      showPieChart: boolean;
    };
    onlineEngagement: {
      livestreamAvgViewers: number;
      youtubeSubscribers: number;
      monthlyWatchHours: number;
      platformImpressions: number;
    };
    enableAnimations: boolean;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const COLORS = ['#3A9188', '#134e2cff', '#A4B0BE', '#2C3E50', '#34495E', '#5DADE2', '#F39C12', '#E74C3C', '#9B59B6', '#1ABC9C'];

const AudienceDemographicsBlock: React.FC<AudienceDemographicsBlockProps> = ({ data, theme }) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data.enableAnimations) {
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (blockRef.current) {
      observer.observe(blockRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, data.enableAnimations]);

  const renderPieChart = (chartData: any[], dataKey: string) => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
         <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey={dataKey}
          isAnimationActive={hasAnimated}
          animationBegin={0}
          animationDuration={1500}
          animationEasing="ease-out"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderPercentageList = (items: any[], labelKey: string) => (
    <Box sx={{ pl: 2 }}>
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
            borderBottom: index < items.length - 1 ? '1px solid #E0E0E0' : 'none'
          }}
        >
          <Typography variant="body1" sx={{ color: '#2C3E50' }}>
            {item[labelKey]}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme?.primaryColor || '#3A9188'
            }}
          >
            {item.percentage}%
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      ref={blockRef}
      sx={{
        py: 8,
        px: 3,
        bgcolor: '#ffffffff'
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: theme?.primaryColor || '#2C3E50',
              mb: 1
            }}
          >
            Who Attends
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Understanding our audience demographics and engagement
          </Typography>
        </Box>

        {/* Age Distribution */}
        {data.ageDistribution.data.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 2,
              bgcolor: '#f9f9f9'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PeopleIcon sx={{ fontSize: 32, color: theme?.primaryColor || '#3A9188', mr: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                Age Distribution
              </Typography>
            </Box>

{data.ageDistribution.showPieChart ? (
  renderPieChart(
    data.ageDistribution.data.map(item => ({ name: item.ageRange, value: item.percentage })),
    'value'
  ) 
            ) : (
              renderPercentageList(data.ageDistribution.data, 'ageRange')
            )}
          </Paper>
        )}

        {/* Professional Profile */}
        {data.professionalProfile.data.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 2,
              bgcolor: '#f9f9f9'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <WorkIcon sx={{ fontSize: 32, color: theme?.primaryColor || '#3A9188', mr: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                Professional Profile
              </Typography>
            </Box>

            {data.professionalProfile.showPieChart ? (
              renderPieChart(
                data.professionalProfile.data.map(item => ({ name: item.category, value: item.percentage })),
                'value'
              )
            ) : (
              renderPercentageList(data.professionalProfile.data, 'category')
            )}
          </Paper>
        )}

        {/* Average Spend - NOW WITH CUSTOM COLOR SUPPORT */}
        {data.averageSpend > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 2,
              // ✅ USE CUSTOM COLOR IF PROVIDED, OTHERWISE FALL BACK TO THEME
              bgcolor: data.averageSpendBgColor || theme?.primaryColor || '#3A9188',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              <AnimatedNumber
                value={data.averageSpend}
                animate={hasAnimated}
                duration={2000}
                prefix="€"
                sx={{ fontSize: '4rem', fontWeight: 700 }}
              />
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Average Spend Per Visit
            </Typography>
         <Typography
          variant="body2"
          sx={{ color: '#ebedd8ff', opacity: 0.8, mt: 1 }}
          >
          Tickets + Bar + Merchandise
          </Typography>
          </Paper>
        )}

        {/* Geographic Reach */}
        {data.geographicReach.data.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 2,
              bgcolor: '#f9f9f9'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PublicIcon sx={{ fontSize: 32, color: theme?.primaryColor || '#3A9188', mr: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                Geographic Reach
              </Typography>
            </Box>

            {data.geographicReach.showPieChart ? (
              renderPieChart(
                data.geographicReach.data.map(item => ({ name: item.location, value: item.percentage })),
                'value'
              )
            ) : (
              renderPercentageList(data.geographicReach.data, 'location')
            )}
          </Paper>
        )}

        {/* Online Engagement */}
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: '#f9f9f9'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#2C3E50',
              mb: 4,
              textAlign: 'center'
            }}
          >
            Online Engagement
          </Typography>

          <Grid container spacing={3}>
            {data.onlineEngagement.livestreamAvgViewers > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <StreamIcon sx={{ fontSize: 40, color: theme?.primaryColor || '#3A9188', mb: 1 }} />
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: theme?.primaryColor || '#3A9188',
                      mb: 1
                    }}
                  >
                    <AnimatedNumber
                      value={data.onlineEngagement.livestreamAvgViewers}
                      animate={hasAnimated}
                      duration={2000}
                      sx={{ 
                        color: theme?.primaryColor || '#3A9188', 
                        fontWeight: 700, 
                        fontSize: '2rem' 
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Livestream Viewers
                  </Typography>
                </Box>
              </Grid>
            )}

            {data.onlineEngagement.youtubeSubscribers > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <YouTubeIcon sx={{ fontSize: 40, color: '#FF0000', mb: 1 }} />
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2C3E50',
                      mb: 1
                    }}
                  >
                    <AnimatedNumber
                      value={data.onlineEngagement.youtubeSubscribers}
                      animate={hasAnimated}
                      duration={2000}
                      sx={{ 
                        color: theme?.primaryColor || '#3A9188', 
                        fontWeight: 700, 
                        fontSize: '2rem' 
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>
                    YouTube Subscribers
                  </Typography>
                </Box>
              </Grid>
            )}

            {data.onlineEngagement.monthlyWatchHours > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <TrendingIcon sx={{ fontSize: 40, color: theme?.primaryColor || '#3A9188', mb: 1 }} />
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2C3E50',
                      mb: 1
                    }}
                  >
                    <AnimatedNumber
                      value={data.onlineEngagement.monthlyWatchHours}
                      animate={hasAnimated}
                      duration={2000}
                      sx={{ 
                        color: theme?.primaryColor || '#3A9188', 
                        fontWeight: 700, 
                        fontSize: '2rem' 
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Monthly Watch Hours
                  </Typography>
                </Box>
              </Grid>
            )}

            {data.onlineEngagement.platformImpressions > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <ViewsIcon sx={{ fontSize: 40, color: theme?.primaryColor || '#3A9188', mb: 1 }} />
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2C3E50',
                      mb: 1
                    }}
                  >
                    <AnimatedNumber
                      value={data.onlineEngagement.platformImpressions}
                      animate={hasAnimated}
                      duration={2000}
                      sx={{ 
                        color: theme?.primaryColor || '#3A9188', 
                        fontWeight: 700, 
                        fontSize: '2rem' 
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Platform Impressions
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

// Animated Number Component
interface AnimatedNumberProps {
  value: number;
  animate: boolean;
  duration?: number;
  prefix?: string;
  suffix?: string;
  sx?: any;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  animate,
  duration = 2000,
  prefix = '',
  suffix = '',
  sx
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!animate) {
      setDisplayValue(value);
      return;
    }

    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateValue = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(easeOutQuart * value);
      
      setDisplayValue(current);

      if (now < endTime) {
        requestAnimationFrame(updateValue);
      } else {
        setDisplayValue(value);
      }
    };

    requestAnimationFrame(updateValue);
  }, [animate, value, duration]);

  return (
    <Typography component="span" sx={sx}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </Typography>
  );
};

export default AudienceDemographicsBlock;